import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserMinus, UserPlus, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getAuthHeader } from '../../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_LABELS = { admin: 'Administrateur', cooper: "COOPER'S", expert: 'Expert', member: 'Membre' };

// Liste des personnes ayant un rôle personnalisé, avec attribution et retrait.
export const RoleUsersDialog = ({ role, onClose, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roles/${role.name}/users`, { headers: getAuthHeader() });
    setUsers(data.users || []);
  }, [role.name]);

  useEffect(() => { load().catch(() => toast.error('Liste indisponible')); }, [load]);

  const chercher = async () => {
    if (search.trim().length < 2) return;
    try {
      const { data } = await axios.get(`${API}/api/invitations/users/list`, { params: { search }, headers: getAuthHeader() });
      setResults((data.users || data || []).filter((u) => u.role !== 'super_admin').slice(0, 8));
    } catch (e) {
      toast.error('Recherche indisponible');
    }
  };

  const changer = async (userId, retirer) => {
    try {
      if (retirer) await axios.delete(`${API}/api/roles/${role.name}/assign/${userId}`, { headers: getAuthHeader() });
      else await axios.post(`${API}/api/roles/${role.name}/assign`, { user_id: userId }, { headers: getAuthHeader() });
      toast.success(retirer ? 'Rôle retiré' : 'Rôle attribué');
      setResults([]);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="role-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Personnes ayant le rôle « {role.display_name} »</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && chercher()}
            placeholder="Rechercher un compte par nom ou email" data-testid="role-users-search" />
          <Button onClick={chercher} variant="outline" data-testid="role-users-search-btn">Rechercher</Button>
        </div>
        {results.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm">
            <span>{u.prenom} {u.nom} · {u.email} · {BASE_LABELS[u.role] || u.role}</span>
            <Button size="sm" onClick={() => changer(u.id, false)} data-testid={`role-assign-${u.id}`}><UserPlus className="mr-1 h-4 w-4" />Attribuer</Button>
          </div>
        ))}
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {users.length === 0 && <p className="text-sm text-slate-500">Personne n’a encore ce rôle.</p>}
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm" data-testid={`role-user-${u.id}`}>
              <span>{u.prenom} {u.nom} · {u.email} · compte {BASE_LABELS[u.role] || u.role}</span>
              <Button size="sm" variant="ghost" className="text-red-700" onClick={() => changer(u.id, true)} data-testid={`role-unassign-${u.id}`}>
                <UserMinus className="mr-1 h-4 w-4" />Retirer
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
